import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

/**
 * Render text containing LaTeX math.
 *
 * Supports:
 *   • Inline math:   wrap expression in $ ... $
 *   • Block math:    wrap expression in $$ ... $$
 *   • Plain text:    rendered as-is
 *
 * Preserves newlines (each newline becomes a line break).
 *
 * Examples:
 *   <MathContent text="The quadratic formula is $x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$." />
 *   <MathContent text="$$E = mc^2$$" />
 */
const MathContent = ({ text, className = "" }) => {
  if (!text) return null;
  if (typeof text !== "string") return <span className={className}>{text}</span>;

  // Tokenize into [{type: 'text'|'inline'|'block', value: string}]
  const tokens = [];
  let rest = text;
  // Match $$...$$ first, then $...$
  const regex = /(\$\$([\s\S]+?)\$\$)|(\$([^$\n]+?)\$)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(rest)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ type: "text", value: rest.slice(lastIndex, m.index) });
    }
    if (m[1]) {
      tokens.push({ type: "block", value: m[2].trim() });
    } else if (m[3]) {
      tokens.push({ type: "inline", value: m[4].trim() });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < rest.length) {
    tokens.push({ type: "text", value: rest.slice(lastIndex) });
  }

  // Render text with newlines preserved
  const renderText = (s, key) => {
    const lines = s.split("\n");
    return lines.map((line, i) => (
      <span key={`${key}-${i}`}>
        {line}
        {i < lines.length - 1 && <br />}
      </span>
    ));
  };

  return (
    <span className={`math-content ${className}`}>
      {tokens.map((tok, i) => {
        try {
          if (tok.type === "block") {
            return (
              <span key={i} className="block my-2">
                <BlockMath math={tok.value} />
              </span>
            );
          }
          if (tok.type === "inline") {
            return <InlineMath key={i} math={tok.value} />;
          }
          return <span key={i}>{renderText(tok.value, i)}</span>;
        } catch (e) {
          // If KaTeX fails to parse, fall back to raw text wrapped in $...$
          return (
            <code key={i} className="text-xs bg-slate-800 px-1 rounded">
              {tok.type === "block" ? `$$${tok.value}$$` : `$${tok.value}$`}
            </code>
          );
        }
      })}
    </span>
  );
};

export default MathContent;
